import { Route } from "@angular/router";
import { AuthGuard } from "app/core/auth/guards/auth.guard";
import { NoAuthGuard } from "app/core/auth/guards/noAuth.guard";
import { LayoutComponent } from "app/layout/layout.component";
import { InitialDataResolver } from "app/app.resolvers";

// @formatter:off
/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/explicit-function-return-type */
export const appRoutes: Route[] = [
  // Redirect empty path to '/example'
  { path: "", pathMatch: "full", redirectTo: "sign-in" },

  // Redirect signed in user to the '/example'
  //
  // After the user signs in, the sign in page will redirect the user to the 'signed-in-redirect'
  // path. Below is another redirection for that path to redirect the user to the desired
  // location. This is a small convenience to keep all main routes together here on this file.
  { path: "signed-in-redirect", pathMatch: "full", redirectTo: "dashboard" },

  // Auth routes for guests
  {
    path: "",
    canActivate: [NoAuthGuard],
    canActivateChild: [NoAuthGuard],
    component: LayoutComponent,
    data: {
      layout: "empty",
    },
    children: [
      {
        path: "confirmation-required",
        loadChildren: () =>
          import(
            "app/modules/auth/confirmation-required/confirmation-required.module"
          ).then((m) => m.AuthConfirmationRequiredModule),
      },
      {
        path: "forgot-password",
        loadChildren: () =>
          import(
            "app/modules/auth/forgot-password/forgot-password.module"
          ).then((m) => m.AuthForgotPasswordModule),
      },
      {
        path: "reset-password",
        loadChildren: () =>
          import("app/modules/auth/reset-password/reset-password.module").then(
            (m) => m.AuthResetPasswordModule
          ),
      },
      {
        path: "sign-in",
        loadChildren: () =>
          import("app/modules/auth/sign-in/sign-in.module").then(
            (m) => m.AuthSignInModule
          ),
      },
      {
        path: "sign-up",
        loadChildren: () =>
          import("app/modules/auth/sign-up/sign-up.module").then(
            (m) => m.AuthSignUpModule
          ),
      },
    ],
  },

  // Auth routes for authenticated users
  {
    path: "",
    canActivate: [AuthGuard],
    canActivateChild: [AuthGuard],
    component: LayoutComponent,
    data: {
      layout: "empty",
    },
    children: [
      {
        path: "sign-out",
        loadChildren: () =>
          import("app/modules/auth/sign-out/sign-out.module").then(
            (m) => m.AuthSignOutModule
          ),
      },
      {
        path: "unlock-session",
        loadChildren: () =>
          import("app/modules/auth/unlock-session/unlock-session.module").then(
            (m) => m.AuthUnlockSessionModule
          ),
      },
    ],
  },

  // Landing routes
  {
    path: "",
    component: LayoutComponent,
    data: {
      layout: "empty",
    },
    children: [
      {
        path: "maintenance",
        loadChildren: () =>
          import("app/modules/auth/maintenance/maintenance.module").then(
            (m) => m.MaintenanceModule
          ),
      },

      {
        path: "contactus",
        loadChildren: () =>
          import("app/modules/auth/contact-us2/contact-us2.module").then(
            (m) => m.ContactUs2Module
          ),
      },
      {
        path: "home",
        loadChildren: () =>
          import("app/modules/landing/home/home.module").then(
            (m) => m.LandingHomeModule
          ),
      },
    ],
  },

  // Admin routes
  {
    path: "",
    canActivate: [AuthGuard],
    canActivateChild: [AuthGuard],
    component: LayoutComponent,
    resolve: {
      initialData: InitialDataResolver,
    },
    children: [
      {
        path: "profile-dashboard",
        loadChildren: () =>
          import(
            "app/modules/admin/admin-dashboard/admin-dashboard.module"
          ).then((m) => m.StaffDashboardModule),
      },
      {
        path: "membership-form",
        loadChildren: () =>
          import(
            "app/modules/admin/admin-dashboard/admin-membership-form/admin-membership-form.module"
          ).then((m) => m.AdminMembershipFormModule),
      },
      {
        path: "admin-profile",
        loadChildren: () =>
          import(
            "app/modules/admin/admin-dashboard/admin-profile/admin-profile.module"
          ).then((m) => m.AdminProfileModule),
      },
      {
        path: "admin-profile/:id",
        loadChildren: () =>
          import(
            "app/modules/admin/admin-dashboard/admin-profile/admin-profile.module"
          ).then((m) => m.AdminProfileModule),
      },
      {
        path: "profile-detail",
        loadChildren: () =>
          import(
            "app/modules/admin/admin-dashboard/admin-detail/admin-detail.module"
          ).then((m) => m.StaffMembershipDetailModule),
      },
      {
        path: "create-program",
        loadChildren: () =>
          import(
            "app/modules/admin/programs/create-program/create-program.module"
          ).then((m) => m.CreateProgramModule),
      },
      {
        path: "create-program/:id",
        loadChildren: () =>
          import(
            "app/modules/admin/programs/create-program/create-program.module"
          ).then((m) => m.CreateProgramModule),
      },
      {
        path: "program-detail/:programId",
        loadChildren: () =>
          import(
            "app/modules/admin/programs/program-detail/program-detail.module"
          ).then((m) => m.ProgramDetailModule),
      },
      {
        path: "program-list",
        loadChildren: () =>
          import(
            "app/modules/admin/programs/programs-list/programs-list.module"
          ).then((m) => m.ProgramsListModule),
      },
      {
        path: "organization",
        loadChildren: () =>
          import("app/modules/admin/organization/organization.module").then(
            (m) => m.OrganizationModule
          ),
      },
      {
        path: "create-community",
        loadChildren: () =>
          import(
            "app/modules/admin/admin-dashboard/create-community/create-community.module"
          ).then((m) => m.CreateCommunityModule),
      },
      {
        path: "community-list",
        loadChildren: () =>
          import(
            "app/modules/admin/admin-dashboard/community-list/community-list.module"
          ).then((m) => m.CommunityListModule),
      },
      {
        path: "community-detail",
        loadChildren: () =>
          import(
            "app/modules/admin/admin-dashboard/community-detail/community-detail.module"
          ).then((m) => m.CommunityDetailModule),
      },
      {
        path: "community-detail/:id",
        loadChildren: () =>
          import(
            "app/modules/admin/admin-dashboard/community-detail/community-detail.module"
          ).then((m) => m.CommunityDetailModule),
      },
      {
        path: "attendance",
        loadChildren: () =>
          import(
            "app/modules/admin/admin-dashboard/attendance-student/attendance-student.module"
          ).then((m) => m.AttendanceStudentModule),
      },
      {
        path: "attendance-detail/:classScheduleId",
        loadChildren: () =>
          import(
            "app/modules/admin/admin-dashboard/attendance-detail/attendance-detail.module"
          ).then((m) => m.AttendanceDetailModule),
      },
      {
        path: "reports",
        loadChildren: () =>
          import(
            "app/modules/admin/admin-dashboard/admin-reports/admin-reports.module"
          ).then((m) => m.AdminReportsModule),
      },
      {
        path: "contact-us",
        loadChildren: () =>
          import("app/modules/auth/contact-us/contact-us.module").then(
            (m) => m.ContactUsModule
          ),
      },
      // using this one in admin dashboard.
      // staff-profile related routings
      {
        path: "staff-form",
        loadChildren: () =>
          import(
            "app/modules/admin/staff-profile/staff-form/staff-form.module"
          ).then((m) => m.StaffFormModule),
      },
      {
        path: "staff-detail/:Id",
        loadChildren: () =>
          import(
            "app/modules/admin/staff-profile/staff-detail/staff-detail.module"
          ).then((m) => m.StaffDetailModule),
      },
    ],
  },
];
